import React from 'react';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import {
  ArrowLeft,
  Shield,
  Mail,
  Server,
  Database,
  Lock,
  Eye,
  Link as LinkIcon,
  FileText,
  UserCheck,
  KeyRound,
  Cookie,
  Smartphone,
  CreditCard,
  Youtube,
  LogIn,
  MessageCircle,
  Video,
  Type,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Datenschutzerklärung | Arabicroots"
        description="Datenschutzerklärung von Arabicroots – Informationen zur Verarbeitung personenbezogener Daten auf unserer Website."
        keywords="Datenschutz, DSGVO, Arabicroots, Datenschutzerklärung"
        url="/datenschutz"
        noIndex={true}
      />

      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center group">
              <img
                src="/LogoHeader4.png"
                alt="Arabicroots Logo"
                className="h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
              />
            </Link>
            <Link
              to="/"
              className="flex items-center text-gray-600 hover:text-[#970000] transition-colors duration-200"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Zurück zur Startseite
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <div className="flex items-center mb-4">
            <Shield className="h-10 w-10 text-[#970000] mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Datenschutzerklärung
            </h1>
          </div>
          <p className="text-lg text-gray-600">
            Informationen zur Verarbeitung personenbezogener Daten auf der Website von Arabicroots.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Stand: 12. Juni 2026
          </p>
        </div>

        <div className="space-y-12">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Allgemeine Hinweise</h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Der Schutz deiner personenbezogenen Daten ist uns ein wichtiges Anliegen.
                Wir verarbeiten deine Daten vertraulich und entsprechend den geltenden
                datenschutzrechtlichen Vorschriften, insbesondere der Datenschutz-Grundverordnung (DSGVO).
              </p>
              <p>
                Diese Datenschutzerklärung informiert dich darüber, welche Daten wir beim Besuch
                unserer Website, bei der Nutzung unseres Kontaktformulars, bei einer Kursanmeldung
                sowie bei der Nutzung des Schülerbereichs und der Lern-App verarbeiten.
              </p>
            </div>
          </section>

          <section className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Mail className="h-6 w-6 text-[#970000] mr-3" />
              Verantwortlicher
            </h2>
            <div className="space-y-2 text-gray-700">
              <p className="font-semibold">Arabicroots</p>
              <p>Abdelilah Zahraoui</p>
              <p>c/o IP-Management #4911</p>
              <p>Ludwig-Erhard-Str. 18</p>
              <p>20459 Hamburg</p>
              <p>Deutschland</p>

              <div className="pt-4 mt-4 border-t border-gray-200 space-y-1">
                <p><strong>E-Mail:</strong> info@arabicroots.de</p>
                <p><strong>Telefon:</strong> +49 178 8378689</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Eye className="h-6 w-6 text-[#970000] mr-3" />
              Welche Daten verarbeitet werden
            </h2>

            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#970000] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span><strong>Technische Zugriffsdaten:</strong> z. B. IP-Adresse, Datum und Uhrzeit des Zugriffs, Browsertyp, Betriebssystem, Referrer-URL und aufgerufene Seiten.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#970000] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span><strong>Kontaktdaten:</strong> z. B. Name, E-Mail-Adresse, Betreff, Nachricht und ggf. kursbezogene Angaben aus dem Kontaktformular.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#970000] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span><strong>Anmeldedaten:</strong> z. B. Vor- und Nachname, E-Mail-Adresse, Telefonnummer, Geschlecht, Alter, Instagram-Name, aktuelles Niveau, gewählter Kurs, gewünschter Startzeitpunkt und Zahlungsart.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#970000] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span><strong>Freiwillige Angaben:</strong> z. B. Newsletter-Einwilligung oder zusätzliche Informationen, die du uns selbst mitteilst.</span>
                </li>
              </ul>
            </div>
          </section>

          <section className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Server className="h-6 w-6 text-[#970000] mr-3" />
                Hosting und Server-Logfiles
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Unsere Website wird bei IONOS gehostet. Beim Aufruf der Website werden durch den
                  Hosting-Anbieter technisch erforderliche Daten verarbeitet, um die Website bereitzustellen,
                  ihre Stabilität zu gewährleisten und Missbrauch oder Angriffe erkennen zu können.
                </p>
                <p>
                  Dazu können insbesondere IP-Adresse, Zeitstempel, Browserinformationen,
                  Betriebssystem, Referrer-URL und angefragte Inhalte gehören.
                </p>
                <p>
                  Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO
                  (berechtigtes Interesse an einem sicheren und funktionsfähigen Webauftritt).
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
              <h3 className="font-semibold text-blue-900 mb-2">Hosting-Anbieter</h3>
              <p className="text-blue-800 text-sm leading-relaxed">
                IONOS / 1&amp;1 IONOS SE, Elgendorfer Straße 57, 56410 Montabaur, Deutschland
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <FileText className="h-6 w-6 text-[#970000] mr-3" />
              Kontaktformular
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Wenn du uns über das Kontaktformular eine Nachricht sendest, verarbeiten wir
                die von dir eingegebenen Daten zur Bearbeitung deiner Anfrage und zur weiteren
                Kommunikation mit dir.
              </p>
              <p>
                Dazu gehören insbesondere dein Name, deine E-Mail-Adresse, der gewählte Betreff,
                gegebenenfalls ein ausgewählter Kurs und deine Nachricht.
              </p>
              <p>
                Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO,
                sofern deine Anfrage auf eine konkrete vorvertragliche Kommunikation gerichtet ist,
                sowie ergänzend auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO
                (berechtigtes Interesse an einer geordneten Bearbeitung von Anfragen).
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <UserCheck className="h-6 w-6 text-[#970000] mr-3" />
              Anmeldeformular für Kurse
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Wenn du dich über unsere Website für einen Kurs anmeldest, verarbeiten wir die von
                dir übermittelten Daten zur Bearbeitung deiner Anmeldung, zur Kontaktaufnahme,
                zur Einschätzung deines Niveaus, zur Kurszuordnung sowie zur organisatorischen Vorbereitung
                der Teilnahme.
              </p>
              <p>
                Die Verarbeitung erfolgt insbesondere auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO
                (vorvertragliche Maßnahmen und Vertragsdurchführung).
              </p>
              <p>
                Soweit du uns freiwillige Zusatzangaben machst, erfolgt die Verarbeitung zusätzlich
                auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO oder Art. 6 Abs. 1 lit. f DSGVO,
                je nach Art der Angabe und Zweck der Verarbeitung.
              </p>
            </div>
          </section>

          <section className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Database className="h-6 w-6 text-[#970000] mr-3" />
                Verarbeitung über Supabase
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Für die Speicherung und Verwaltung von Anfragen, Anmeldungen, Nutzerkonten und Kursdaten
                  nutzen wir Supabase als technische Datenbank- und Backend-Lösung.
                  Anbieter ist Supabase, Inc. (USA).
                </p>
                <p>
                  Die von dir über Formulare eingegebenen Daten können daher in unserer Supabase-Datenbank
                  gespeichert und dort zur Bearbeitung deiner Anfrage oder Anmeldung verarbeitet werden.
                </p>
                <p>
                  Die Verarbeitung erfolgt im Rahmen eines Auftragsverarbeitungsverhältnisses nach Art. 28 DSGVO.
                  Soweit dabei eine Verarbeitung in Drittländern, insbesondere in den USA, nicht ausgeschlossen
                  werden kann, erfolgt diese auf Grundlage geeigneter Garantien im Sinne von Art. 44 ff. DSGVO.
                </p>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
              <h3 className="font-semibold text-green-900 mb-2">Hinweis zu Dienstleistern</h3>
              <p className="text-green-800 text-sm leading-relaxed">
                Wir wählen unsere technischen Dienstleister sorgfältig aus und beschränken den Zugriff
                auf personenbezogene Daten auf das erforderliche Maß.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <KeyRound className="h-6 w-6 text-[#970000] mr-3" />
              Schülerbereich und Nutzerkonto
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Eingeschriebene Schülerinnen und Schüler erhalten Zugang zu einem persönlichen
                Schülerbereich. Dort werden Kursaufzeichnungen, Materialien, Live-Termine,
                Lernfortschritt und Zertifikate bereitgestellt.
              </p>
              <p>
                Für den Betrieb des Schülerbereichs verarbeiten wir folgende Daten:
              </p>
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-[#970000] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span><strong>Zugangsdaten:</strong> E-Mail-Adresse und verschlüsseltes Passwort zur Authentifizierung.</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-[#970000] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span><strong>Sitzungsdaten:</strong> Technische Session-Tokens, die beim Login erzeugt und zur Aufrechterhaltung der Anmeldung verwendet werden.</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-[#970000] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span><strong>Nutzungsdaten:</strong> Z. B. Lernfortschritt, abgeschlossene Einheiten und Kursaktivität innerhalb des Schülerbereichs.</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-[#970000] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span><strong>Metadaten:</strong> Datum und Uhrzeit der letzten Anmeldung sowie technische Zugriffsdaten.</span>
                  </li>
                </ul>
              </div>
              <p>
                Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO
                (Vertragserfüllung) sowie Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse
                an einem sicheren, funktionsfähigen Lernbereich).
              </p>
              <p>
                Für die Authentifizierung und Verwaltung von Nutzerkonten nutzen wir Supabase Auth.
                Dabei werden E-Mail-Adresse und Passwort-Hash in der Supabase-Datenbank gespeichert
                und verarbeitet. Es gelten die Bedingungen des bestehenden
                Auftragsverarbeitungsverhältnisses mit Supabase (s. u.).
              </p>
              <p>
                Das Nutzerkonto wird nach Beendigung des Kursverhältnisses gesperrt oder auf Anfrage
                gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
                Du kannst die Löschung deines Kontos jederzeit per E-Mail an info@arabicroots.de beantragen.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Cookie className="h-6 w-6 text-[#970000] mr-3" />
              Cookies und Session-Tokens
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Beim Besuch unserer öffentlichen Website setzen wir keine Tracking- oder
                Marketing-Cookies ein. Es werden lediglich technisch notwendige Prozesse
                ausgeführt, die für den Betrieb der Website erforderlich sind.
              </p>
              <p>
                Im Schülerbereich werden nach dem Login technisch notwendige Session-Tokens
                gesetzt. Diese dienen ausschließlich dazu, deine Anmeldung aufrechtzuerhalten
                und dich beim nächsten Besuch wiederzuerkennen. Ohne diese Tokens ist eine
                Nutzung des geschützten Bereichs nicht möglich.
              </p>
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-[#970000] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span><strong>Art:</strong> Session-Token (Local Storage / httpOnly Cookie), gesetzt durch Supabase Auth.</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-[#970000] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span><strong>Zweck:</strong> Technisch notwendige Authentifizierung im Schülerbereich.</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-[#970000] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span><strong>Speicherdauer:</strong> Bis zur Abmeldung oder nach Ablauf der Sitzung (in der Regel max. 1 Woche).</span>
                  </li>
                </ul>
              </div>
              <p>
                Zusätzlich werden Eingaben im Anmeldeformular lokal in deinem Browser (Local Storage)
                zwischengespeichert, damit sie beim versehentlichen Verlassen der Seite nicht verloren gehen.
                Diese Daten verbleiben ausschließlich auf deinem Gerät, werden nicht an uns oder Dritte
                übertragen und nicht zu Tracking-Zwecken verwendet.
              </p>
              <p>
                Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO
                (Vertragserfüllung) sowie Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse
                an einem sicheren Login-Verfahren). Die Speicherung bzw. das Auslesen von Informationen
                auf deinem Endgerät erfolgt nur, soweit dies für den von dir gewünschten Dienst
                technisch erforderlich ist (§ 25 Abs. 2 TDDDG).
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Type className="h-6 w-6 text-[#970000] mr-3" />
              Schriftarten (Web Fonts)
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Die auf dieser Website verwendeten Schriftarten sind lokal auf unserem Server eingebunden.
                Beim Laden der Schriften wird keine Verbindung zu Servern von Google oder anderen
                Drittanbietern aufgebaut; es werden dabei keine personenbezogenen Daten an Dritte übertragen.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Smartphone className="h-6 w-6 text-[#970000] mr-3" />
              Lern-App
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Die Arabicroots Lern-App steht ausschließlich eingeschriebenen Schülerinnen und
                Schülern zur Verfügung und ist Bestandteil des Schülerbereichs. Ein eigenständiger
                Account ist nicht erforderlich — der Zugang erfolgt über das bestehende Nutzerkonto.
              </p>
              <p>
                Über die App werden folgende Daten verarbeitet:
              </p>
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-[#970000] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span><strong>Lernfortschritt:</strong> Welche Vokabeln geübt oder gelernt wurden, Wiederholungsstand und Karteikartenhistorie.</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-[#970000] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span><strong>Nutzungsdaten:</strong> Lernaktivität innerhalb der App (z. B. Sitzungsdauer, aufgerufene Inhalte).</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-[#970000] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span><strong>Gerätedaten:</strong> Technische Informationen wie Betriebssystemversion und Gerätetyp, soweit für Kompatibilität erforderlich.</span>
                  </li>
                </ul>
              </div>
              <p>
                Diese Daten werden in der Supabase-Datenbank gespeichert und ausschließlich zur
                Bereitstellung und Verbesserung des Lernangebots genutzt. Sie werden nicht an Dritte
                weitergegeben und nicht zu Werbezwecken verwendet.
              </p>
              <p>
                Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO
                (Vertragserfüllung) sowie Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse
                an der Bereitstellung eines personalisierten Lernangebots).
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <CreditCard className="h-6 w-6 text-[#970000] mr-3" />
              Zahlungsabwicklung über Stripe
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Kursgebühren können per Überweisung oder online bezahlt werden. Nur soweit du eine
                Online-Zahlung über den Schülerbereich nutzt, erfolgt die Abwicklung über den
                Zahlungsdienstleister Stripe. Anbieter für Kunden in der EU ist die Stripe Payments
                Europe, Ltd., 1 Grand Canal Street Lower, Grand Canal Dock, Dublin, Irland;
                Muttergesellschaft ist die Stripe, Inc. (USA).
              </p>
              <p>
                Bei Zahlung per Überweisung ist Stripe nicht beteiligt. In diesem Fall verarbeiten
                wir lediglich die üblichen Zahlungsdaten (z. B. Name, Betrag, Verwendungszweck,
                Bankverbindung) zur Vertragsabwicklung und aufgrund gesetzlicher
                Aufbewahrungspflichten (Art. 6 Abs. 1 lit. b und c DSGVO).
              </p>
              <p>
                Wenn du eine Zahlung über Stripe durchführst, werden die für die Zahlungsabwicklung
                erforderlichen Daten (z. B. Name, E-Mail-Adresse, Zahlungsinformationen, Betrag) direkt
                an Stripe übermittelt und dort verarbeitet. Deine vollständigen Zahlungsdaten
                (z. B. Kartennummern) werden nicht auf unseren Systemen gespeichert.
              </p>
              <p>
                Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO
                (Vertragserfüllung). Eine Verarbeitung in Drittländern, insbesondere in den USA,
                ist möglich und erfolgt auf Grundlage geeigneter Garantien im Sinne von Art. 44 ff. DSGVO.
              </p>
              <p>
                Weitere Informationen findest du in der Datenschutzerklärung von Stripe:
                {' '}
                <a
                  href="https://stripe.com/de/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#970000] hover:underline"
                >
                  https://stripe.com/de/privacy
                </a>
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Mail className="h-6 w-6 text-[#970000] mr-3" />
              E-Mail-Versand über Resend
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Für den Versand von Transaktions-E-Mails, insbesondere Bestätigungs- und Benachrichtigungs-E-Mails
                nach dem Absenden von Formularen, nutzen wir den Dienst Resend.
              </p>
              <p>
                Anbieter ist die Resend Inc., 2261 Market Street #5039, San Francisco, CA 94114, USA.
              </p>
              <p>
                Wenn du über unsere Website ein Formular absendest, können die von dir eingegebenen Daten,
                insbesondere dein Name und deine E-Mail-Adresse, an Resend übermittelt werden, damit die
                entsprechenden E-Mails zuverlässig versendet werden können.
              </p>
              <p>
                Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO
                (Vertragserfüllung bzw. vorvertragliche Maßnahmen) sowie Art. 6 Abs. 1 lit. f DSGVO
                (berechtigtes Interesse an einer zuverlässigen und effizienten Kommunikation).
              </p>
              <p>
                Eine Verarbeitung personenbezogener Daten kann dabei auch in Drittländern, insbesondere in den USA,
                stattfinden. Soweit erforderlich, erfolgt dies auf Grundlage geeigneter Garantien im Sinne von
                Art. 44 ff. DSGVO.
              </p>
              <p>
                Weitere Informationen zum Datenschutz bei Resend findest du unter:
                {' '}
                <a
                  href="https://resend.com/legal/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#970000] hover:underline"
                >
                  https://resend.com/legal/privacy-policy
                </a>
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Youtube className="h-6 w-6 text-[#970000] mr-3" />
              YouTube-Videos im Schülerbereich
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Im passwortgeschützten Schülerbereich stellen wir Kursaufzeichnungen über eingebettete
                YouTube-Videos bereit. Anbieter ist die Google Ireland Limited, Gordon House, Barrow Street,
                Dublin 4, Irland; Muttergesellschaft ist die Google LLC (USA).
              </p>
              <p>
                Wir binden die Videos im sogenannten erweiterten Datenschutzmodus
                (youtube-nocookie.com) ein. Dabei werden nach Angaben von YouTube vor dem Abspielen
                eines Videos keine Cookies gesetzt. Beim Laden und Abspielen eines Videos werden jedoch
                technische Daten (z. B. IP-Adresse, Geräteinformationen) an Google übermittelt; eine
                Verarbeitung in den USA ist dabei möglich.
              </p>
              <p>
                Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, da die
                Bereitstellung der Kursaufzeichnungen Teil der vertraglich geschuldeten Leistung ist.
                Auf der öffentlichen Website werden keine YouTube-Videos eingebettet.
              </p>
              <p>
                Weitere Informationen findest du in der Datenschutzerklärung von Google:
                {' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#970000] hover:underline"
                >
                  https://policies.google.com/privacy
                </a>
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Video className="h-6 w-6 text-[#970000] mr-3" />
              Unterrichtsaufzeichnungen
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Unsere Live-Sessions werden aufgezeichnet und den Teilnehmenden des jeweiligen Kurses
                ausschließlich im Schülerbereich zur Verfügung gestellt. Der Unterricht findet grundsätzlich
                ohne Teilnehmer-Kamera statt; in den Aufzeichnungen können daher in der Regel nur
                Wortbeiträge (Stimme) und ggf. der Anzeigename von Teilnehmenden enthalten sein.
              </p>
              <p>
                Die Teilnahme mit Mikrofon ist freiwillig: Wer auch mit der Stimme nicht in der
                Aufzeichnung erscheinen möchte, kann das Mikrofon während der Sessions deaktiviert
                lassen und Fragen z. B. über den Chat stellen.
              </p>
              <p>
                Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO (Bereitstellung
                der Aufzeichnungen als Teil der Kursleistung) sowie Art. 6 Abs. 1 lit. f DSGVO
                (berechtigtes Interesse daran, verpassten Unterricht nachholbar zu machen).
                Die Aufzeichnungen werden nicht öffentlich zugänglich gemacht.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <LogIn className="h-6 w-6 text-[#970000] mr-3" />
              Login über Google oder Apple
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Für den Schülerbereich bieten wir optional die Anmeldung über bestehende Google- oder
                Apple-Konten an (Single Sign-On). Anbieter sind die Google Ireland Limited
                (Gordon House, Barrow Street, Dublin 4, Irland) bzw. die Apple Distribution
                International Ltd. (Hollyhill Industrial Estate, Cork, Irland).
              </p>
              <p>
                Wenn du dich für diese Anmeldeart entscheidest, erhalten wir vom jeweiligen Anbieter
                die für die Kontoerstellung erforderlichen Daten (insbesondere Name und E-Mail-Adresse).
                Umgekehrt erfährt der Anbieter, dass du dich bei uns anmeldest. Es gelten ergänzend die
                Datenschutzbestimmungen des jeweiligen Anbieters.
              </p>
              <p>
                Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO
                (Bereitstellung des von dir gewählten Login-Verfahrens). Die Nutzung ist freiwillig –
                alternativ kannst du dich jederzeit mit E-Mail-Adresse und Passwort registrieren.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <MessageCircle className="h-6 w-6 text-[#970000] mr-3" />
              Kommunikation über WhatsApp
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Für die Kursorganisation und die Kommunikation mit Teilnehmenden nutzen wir neben
                E-Mail auch WhatsApp, einschließlich kursbezogener WhatsApp-Gruppen. Anbieter ist die
                WhatsApp Ireland Limited, 4 Grand Canal Square, Dublin 2, Irland (Teil der Meta-Gruppe);
                eine Verarbeitung in Drittländern, insbesondere in den USA, ist möglich.
              </p>
              <p>
                Wenn du mit uns über WhatsApp kommunizierst oder einer Kursgruppe beitrittst, verarbeitet
                WhatsApp deine Telefonnummer und Metadaten der Kommunikation. In Gruppen ist deine
                Telefonnummer für andere Gruppenmitglieder sichtbar.
              </p>
              <p>
                Die Nutzung von WhatsApp ist freiwillig. Wenn du WhatsApp nicht nutzen möchtest,
                erhältst du alle kursrelevanten Informationen alternativ per E-Mail oder über den
                Schülerbereich. Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO
                (Kursorganisation) bzw. Art. 6 Abs. 1 lit. a DSGVO (freiwilliger Gruppenbeitritt).
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <LinkIcon className="h-6 w-6 text-[#970000] mr-3" />
              Externe Links und Social Media
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Auf unserer Website befinden sich Links zu externen Plattformen, insbesondere zu
                Instagram, YouTube und TikTok.
              </p>
              <p>
                Auf der öffentlichen Website handelt es sich dabei ausschließlich um normale
                Verlinkungen – beim bloßen Besuch unserer Website werden dadurch noch keine Daten an
                diese Anbieter übermittelt. Eingebettete Inhalte gibt es nur im passwortgeschützten
                Schülerbereich (siehe Abschnitt „YouTube-Videos im Schülerbereich“).
              </p>
              <p>
                Erst wenn du einen solchen Link anklickst, verlässt du unsere Website und rufst die jeweilige
                externe Plattform auf. Ab diesem Zeitpunkt gelten die Datenschutzbestimmungen des jeweiligen
                Anbieters.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Newsletter und freiwillige Einwilligungen</h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Soweit auf unserer Website eine freiwillige Einwilligung für spätere Informationen zu
                Kursen, Angeboten oder Neuigkeiten abgefragt wird, erfolgt die Verarbeitung ausschließlich
                auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO.
              </p>
              <p>
                Eine erteilte Einwilligung kannst du jederzeit mit Wirkung für die Zukunft widerrufen.
              </p>
            </div>
          </section>

          <section className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Lock className="h-6 w-6 text-[#970000] mr-3" />
              Rechtsgrundlagen der Verarbeitung
            </h2>

            <div className="space-y-4 text-gray-700">
              <p>
                <strong>Art. 6 Abs. 1 lit. b DSGVO:</strong> Verarbeitung zur Durchführung
                vorvertraglicher Maßnahmen und zur Bearbeitung von Kursanmeldungen.
              </p>
              <p>
                <strong>Art. 6 Abs. 1 lit. a DSGVO:</strong> Verarbeitung auf Grundlage
                einer freiwilligen Einwilligung, z. B. bei optionalen Angaben oder künftigen Informationen.
              </p>
              <p>
                <strong>Art. 6 Abs. 1 lit. f DSGVO:</strong> Verarbeitung auf Grundlage unseres
                berechtigten Interesses an einem sicheren, stabilen und funktionierenden Webauftritt
                sowie an einer geordneten Kommunikation mit Interessenten und Teilnehmern.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Speicherdauer</h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Wir speichern personenbezogene Daten nur so lange, wie dies für die jeweiligen Zwecke
                erforderlich ist oder gesetzliche Aufbewahrungspflichten bestehen.
              </p>
              <p>
                Daten aus Kontaktanfragen werden gelöscht, sobald die Anfrage abschließend bearbeitet wurde
                und keine gesetzlichen oder berechtigten Gründe für eine weitere Speicherung bestehen.
              </p>
              <p>
                Daten aus Kursanmeldungen werden so lange gespeichert, wie dies für die Bearbeitung der
                Anmeldung, die Durchführung organisatorischer Abläufe, die Vertragsabwicklung oder aufgrund
                gesetzlicher Aufbewahrungspflichten erforderlich ist.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Deine Rechte</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Auskunft über deine gespeicherten Daten',
                'Berichtigung unrichtiger Daten',
                'Löschung deiner Daten',
                'Einschränkung der Verarbeitung',
                'Widerspruch gegen die Verarbeitung',
                'Datenübertragbarkeit'
              ].map((item, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-4 text-gray-700">
                  {item}
                </div>
              ))}
            </div>

            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-2xl p-6">
              <p className="text-blue-900 text-sm leading-relaxed mb-3">
                Außerdem hast du das Recht, dich bei einer Datenschutzaufsichtsbehörde zu beschweren,
                wenn du der Ansicht bist, dass die Verarbeitung deiner personenbezogenen Daten
                nicht rechtmäßig erfolgt.
              </p>
              <p className="text-blue-900 text-sm leading-relaxed">
                Für Datenschutzanfragen erreichst du uns unter <strong>info@arabicroots.de</strong>.
              </p>
            </div>
          </section>

          <section className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Datensicherheit</h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Wir treffen technische und organisatorische Maßnahmen, um deine Daten gegen Verlust,
                Manipulation, unbefugten Zugriff oder sonstige unzulässige Verarbeitungen zu schützen.
              </p>
              <p>
                Dazu gehören insbesondere der geschützte Zugriff auf interne Systeme, die Nutzung
                sicherer Verbindungen sowie die Beschränkung des Zugriffs auf notwendige Personen und Systeme.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Änderungen dieser Datenschutzerklärung</h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den
                aktuellen rechtlichen Anforderungen sowie den tatsächlichen Funktionen unserer Website entspricht.
              </p>
              <p>
                Es gilt jeweils die zum Zeitpunkt des Besuchs aktuelle Fassung.
              </p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
